import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Activity, Target, Clock, RefreshCw, Loader2, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiGet } from '../data/api';

function formatOdds(v) {
  return v > 0 ? v.toFixed(2) : '—';
}
function formatPct(v) {
  return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—';
}

// Auto-refresh interval (ms). Keeps the "Ultimo refresh" timestamp fresh
// without forcing the user to reload the page (root-cause of the 13:17
// frozen-timestamp bug observed in production 2026-04-28).
const DASHBOARD_POLL_MS = 60_000;

export default function TTDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uiSyncedAt, setUiSyncedAt] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const pollRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      // Cache-busting query param prevents intermediary CDNs / service
      // workers from serving a stale 200. Backend already sends
      // Cache-Control: no-store, but this is belt-and-suspenders.
      const result = await apiGet(`/tt/dashboard?_=${Date.now()}`);
      setData(result);
      setUiSyncedAt(new Date());
    } catch (err) {
      console.error('TTDashboard fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard().finally(() => setLoading(false));
    pollRef.current = setInterval(fetchDashboard, DASHBOARD_POLL_MS);
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      clearInterval(tick);
    };
  }, [fetchDashboard]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchDashboard();
    setRefreshing(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-zinc-500" size={24} />
    </div>
  );

  if (!data) return <div className="text-zinc-500 text-center py-12">Errore nel caricamento</div>;

  const { scheduled_matches, live_matches, finished_matches, total_players, total_tournaments, value_matches, last_refresh } = data;

  // Seconds since UI last successfully fetched the dashboard payload.
  // Uses `nowTs` (1Hz heartbeat) so the label stays live even between polls.
  const uiAgeSec = uiSyncedAt ? Math.max(0, Math.floor((nowTs - uiSyncedAt.getTime()) / 1000)) : null;
  const uiAgeLabel = uiAgeSec == null
    ? '—'
    : uiAgeSec < 60
      ? `${uiAgeSec}s fa`
      : `${Math.floor(uiAgeSec / 60)}m ${uiAgeSec % 60}s fa`;

  return (
    <div data-testid="tt-dashboard" className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Table Tennis Prematch
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Analisi value betting - Solo dati reali</p>
        </div>
        <div className="flex items-center gap-3">
          {last_refresh && (
            <div data-testid="tt-dashboard-last-refresh" className="text-xs text-zinc-500 flex items-center gap-1.5">
              <Clock size={12} />
              Ultimo refresh: {new Date(last_refresh.finished_at).toLocaleString('it-IT')}
            </div>
          )}
          <div className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="tt-dashboard-ui-sync">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${uiAgeSec != null && uiAgeSec < 90 ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            UI: {uiAgeLabel}
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            data-testid="tt-dashboard-refresh-btn"
            className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1.5 px-2 py-1 rounded border border-zinc-800 hover:border-zinc-700 disabled:opacity-50"
            title="Ricarica i dati ora"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Aggiorno…' : 'Aggiorna'}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox icon={Target} label="Match Prematch" value={scheduled_matches} color="emerald" />
        <StatBox icon={Activity} label="Match Live" value={live_matches} color="blue" />
        <StatBox icon={TrendingUp} label="Con Value" value={value_matches?.length || 0} color="amber" />
        <StatBox icon={RefreshCw} label="Giocatori" value={total_players} color="zinc" />
      </div>

      {/* Value Matches Table */}
      <div className="rounded-sm" style={{ background: '#18181B', border: '1px solid #27272A' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #27272A' }}>
          <h2 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">
            Opportunita di Value
          </h2>
          <Link
            to="/tt/matches"
            data-testid="view-all-matches"
            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
          >
            Tutti i match <ChevronRight size={12} />
          </Link>
        </div>

        {(!value_matches || value_matches.length === 0) ? (
          <div className="px-5 py-12 text-center">
            <AlertTriangle size={28} className="text-zinc-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">Nessuna opportunita di value al momento</p>
            <p className="text-xs text-zinc-600 mt-1">I match con quote bookmaker vengono analizzati automaticamente</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-5 py-3 font-medium">Match</th>
                  <th className="text-left px-3 py-3 font-medium">Torneo</th>
                  <th className="text-right px-3 py-3 font-medium">Quota BK</th>
                  <th className="text-right px-3 py-3 font-medium">Fair Odds</th>
                  <th className="text-right px-3 py-3 font-medium">Edge</th>
                  <th className="text-right px-3 py-3 font-medium">EV</th>
                  <th className="text-right px-3 py-3 font-medium">Conf.</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {value_matches.map((vm) => {
                  const isP1 = vm.best_side === vm.match_name.split(' vs ')[0];
                  const bkOdd = isP1 ? vm.bk_odd_p1 : vm.bk_odd_p2;
                  const fairOdd = isP1 ? vm.fair_odd_p1 : vm.fair_odd_p2;
                  return (
                    <tr key={vm.match_id} className="border-t border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-5 py-3">
                        <div className="text-zinc-200 font-medium">{vm.match_name}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          Selezione: <span className="text-emerald-400">{vm.best_side}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-zinc-400 text-xs">{vm.tournament}</td>
                      <td className="px-3 py-3 text-right font-mono text-zinc-300">{formatOdds(bkOdd)}</td>
                      <td className="px-3 py-3 text-right font-mono text-zinc-400">{formatOdds(fairOdd)}</td>
                      <td className="px-3 py-3 text-right">
                        <span className={`font-mono font-semibold ${vm.best_edge > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatPct(vm.best_edge)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className={`font-mono font-semibold ${vm.best_ev > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatPct(vm.best_ev)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <ConfBadge score={vm.confidence} />
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to={`/tt/matches/${encodeURIComponent(vm.match_id)}`}
                          data-testid={`view-match-${vm.match_id}`}
                          className="text-xs text-zinc-500 hover:text-emerald-400"
                        >
                          <ChevronRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Refresh Log */}
      {last_refresh && (
        <div className="rounded-sm p-4 text-xs text-zinc-500" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <span className="text-zinc-400 font-medium">Ultimo refresh:</span>{' '}
          {last_refresh.matches_found} match trovati, {last_refresh.odds_inserted} quote, {last_refresh.models_run} modelli eseguiti
          {last_refresh.errors_count > 0 && (
            <span className="text-amber-400 ml-2">({last_refresh.errors_count} errori)</span>
          )}
          <span className="ml-2 text-zinc-600">({last_refresh.duration_ms}ms)</span>
        </div>
      )}
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  const colorMap = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    zinc: 'text-zinc-400',
  };
  return (
    <div className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
        <Icon size={14} className={colorMap[color]} />
      </div>
      <div className="text-2xl font-bold text-zinc-50 font-mono">{value}</div>
    </div>
  );
}

function ConfBadge({ score }) {
  const pct = Math.round(score * 100);
  const cls = pct >= 60 ? 'bg-emerald-500/15 text-emerald-400' :
              pct >= 30 ? 'bg-amber-500/15 text-amber-400' :
                          'bg-zinc-500/15 text-zinc-400';
  return <span className={`px-1.5 py-0.5 text-xs rounded font-mono ${cls}`}>{pct}%</span>;
}
